import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowUpRight, ChevronRight, BookOpen } from "lucide-react";
import { INSIGHTS_BY_SLUG, CATEGORIES, INSIGHTS } from "../lib/insightsData";
import { useSEO } from "../lib/useSEO";
import { organisationSchema, SITE_URL, COMPANY } from "../lib/seo";

export default function InsightDetailPage() {
    const { slug } = useParams();
    const article = INSIGHTS_BY_SLUG[slug];

    useSEO({
        title: article ? `${article.title} | Insights — Optimum Hospitality Solutions` : "Insight",
        description: article?.excerpt,
        path: `/insights/${slug}`,
    });

    if (!article) return <Navigate to="/insights" replace />;

    const categoryLabel = CATEGORIES.find((c) => c.slug === article.category)?.label;
    const related = INSIGHTS.filter((a) => a.slug !== slug && a.category === article.category).slice(0, 2);

    const articleSchema = {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: article.title,
        description: article.excerpt,
        datePublished: article.publishedAt,
        author: { "@type": "Person", name: "Sidney Adams" },
        publisher: { "@id": `${SITE_URL}/#organization` },
        mainEntityOfPage: `${SITE_URL}/insights/${slug}`,
        articleSection: categoryLabel,
    };

    const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: "Insights", item: `${SITE_URL}/insights` },
            { "@type": "ListItem", position: 3, name: article.title, item: `${SITE_URL}/insights/${slug}` },
        ],
    };

    return (
        <div data-testid={`insight-page-${slug}`}>
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(organisationSchema) }} />

            <nav className="border-b border-brand-border bg-brand-stone/60">
                <div className="mx-auto max-w-4xl px-6 lg:px-12 py-4 flex items-center gap-2 text-xs text-brand-ink/60">
                    <Link to="/" className="hover:text-brand-navy">Home</Link>
                    <ChevronRight size={12} className="text-brand-ink/40" />
                    <Link to="/insights" className="hover:text-brand-navy">Insights</Link>
                    <ChevronRight size={12} className="text-brand-ink/40" />
                    <span className="text-brand-navy font-medium truncate">{article.title}</span>
                </div>
            </nav>

            <article className="border-b border-brand-border">
                <div className="mx-auto max-w-4xl px-6 lg:px-12 py-16 lg:py-24">
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-brand-champagne">
                        <BookOpen size={12} />
                        {categoryLabel}
                        <span className="text-brand-ink/30">·</span>
                        <span className="text-brand-ink/55 normal-case tracking-normal">{article.readMinutes} min read</span>
                        <span className="text-brand-ink/30">·</span>
                        <time dateTime={article.publishedAt} className="text-brand-ink/55 normal-case tracking-normal">
                            {new Date(article.publishedAt).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
                        </time>
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05] tracking-tight text-brand-navy">{article.title}</h1>
                    <p className="mt-7 text-lg leading-relaxed text-brand-ink/75">{article.excerpt}</p>

                    <div className="mt-12 space-y-10">
                        {article.body.map((section) => (
                            <section key={section.h}>
                                <h2 className="font-display text-2xl text-brand-navy">{section.h}</h2>
                                <p className="mt-4 text-brand-ink/80 leading-relaxed">{section.p}</p>
                            </section>
                        ))}
                    </div>

                    <div className="mt-16 pt-8 border-t border-brand-border text-sm text-brand-ink/70 leading-relaxed">
                        <span className="text-brand-navy font-medium">Written by Sidney Adams</span>, founder of {COMPANY.legalName}. {COMPANY.legalName} is a UK hospitality consultancy supporting hotel owners, general managers and operations directors with operational performance, labour productivity and guest experience.
                    </div>
                </div>
            </article>

            {related.length > 0 && (
                <section className="border-b border-brand-border">
                    <div className="mx-auto max-w-4xl px-6 lg:px-12 py-16">
                        <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne">More from {categoryLabel}</span>
                        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                            {related.map((r) => (
                                <Link key={r.slug} to={`/insights/${r.slug}`} className="border border-brand-border bg-brand-stone p-6 hover:border-brand-champagne transition-colors">
                                    <h3 className="font-display text-lg text-brand-navy leading-snug">{r.title}</h3>
                                    <p className="mt-3 text-sm text-brand-ink/70 leading-relaxed">{r.excerpt.slice(0, 140)}…</p>
                                    <span className="mt-4 inline-flex items-center gap-1 text-sm text-brand-navy link-underline">Read <ArrowUpRight size={14} /></span>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            <section className="bg-brand-navy">
                <div className="mx-auto max-w-4xl px-6 lg:px-12 py-16 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
                    <div className="md:col-span-8">
                        <h2 className="font-display text-2xl sm:text-3xl text-brand-stone leading-tight">
                            Want this kind of thinking applied to your property?
                        </h2>
                        <p className="mt-3 text-brand-stone/75">
                            Book a confidential consultation with Optimum Hospitality Solutions.
                        </p>
                    </div>
                    <div className="md:col-span-4 md:text-right">
                        <Link to="/booking" className="inline-flex items-center gap-2 px-6 py-3 bg-brand-champagne text-brand-navy text-sm tracking-wide hover:bg-white">
                            Book a Consultation <ArrowUpRight size={16} />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
